import { useState, useEffect, useRef, useCallback } from 'react';
import ReferralModal from '../modals/ReferralModal';
import { Outlet, Link, useLocation, useNavigate } from 'react-router-dom';
import DemoBanner from "../common/DemoBanner";
import TestTierToggle from '../sandbox/TestTierToggle';
import SandboxBanner from '../SandboxBanner';
import { useDocumentTitle } from '../../hooks/useDocumentTitle';
import { useAuth } from '../../hooks/useAuth';
import { api } from '../../services/api';
import { getUnreadCount } from '../../utils/notificationState';
import IntercomWidget from '../common/IntercomWidget';
import styles from './DashboardLayout.module.css';

const DashboardLayout = () => {
  // Notification unread count (persisted via localStorage)
  const [unreadNotifCount, setUnreadNotifCount] = useState(() => getUnreadCount());
  useEffect(() => {
    const handleChange = () => setUnreadNotifCount(getUnreadCount());
    window.addEventListener('notif-read-change', handleChange);
    return () => window.removeEventListener('notif-read-change', handleChange);
  }, []);

  const { user, isDemo, logout, isAdmin, sandboxMode, refreshUser } = useAuth();
  // Prefixes document.title with [SANDBOX] for the whole member portal in sandbox.
  useDocumentTitle();
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [isScrolled, setIsScrolled] = useState(false);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [referralModalOpen, setReferralModalOpen] = useState(false);
  const [isExitingTestMode, setIsExitingTestMode] = useState(false);
  const navRef = useRef(null);
  const dropdownRef = useRef(null);

  const updateScrollIndicators = useCallback(() => {
    const nav = navRef.current;
    if (!nav) return;

    const { scrollLeft, scrollWidth, clientWidth } = nav;
    setCanScrollLeft(scrollLeft > 5);
    setCanScrollRight(scrollLeft < scrollWidth - clientWidth - 5);
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 10);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const nav = navRef.current;
    if (!nav) return;

    updateScrollIndicators();
    nav.addEventListener('scroll', updateScrollIndicators);
    window.addEventListener('resize', updateScrollIndicators);

    return () => {
      nav.removeEventListener('scroll', updateScrollIndicators);
      window.removeEventListener('resize', updateScrollIndicators);
    };
  }, [updateScrollIndicators]);

  // Click outside handler for dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setDropdownOpen(false);
      }
    };

    if (dropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [dropdownOpen]);

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const handleDropdownClick = () => {
    setDropdownOpen(!dropdownOpen);
  };

  // Return leg out of the sandbox, attached to the existing "Admin Panel" link.
  // It intercepts ONLY while sandboxMode is true — a normal admin keeps today's plain <Link>
  // navigation with no extra request. Server state is cleared first, then refreshUser()
  // re-reads /auth/me so the banner disappears from real state, never optimistically.
  // Exiting returns this client to the LIVE surface. The POST is pinned live in
  // web/services/api.js so the way OUT never depends on the sandbox surface being
  // healthy; refreshUser() then clears sandboxMode, which clears the surface.
  // Fail-closed: stay put on error — a silent failure must not look like an exit.
  const handleExitTestDashboard = async (event) => {
    if (!sandboxMode || isExitingTestMode) return;
    event.preventDefault();
    setIsExitingTestMode(true);
    try {
      await api.post('/admin/test-admin/exit', {});
      await refreshUser();
      navigate('/admin');
    } catch {
      // Stay put on failure: exiting is what un-stamps future writes, so a silent failure
      // must not be allowed to look like a successful exit.
    } finally {
      setIsExitingTestMode(false);
    }
  };

  const handleMenuItemClick = (path) => {
    setDropdownOpen(false);
    navigate(path);
  };

  const navItems = [
    { path: '/dashboard', label: 'Dashboard', icon: (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <rect x="3" y="3" width="7" height="7" rx="1"></rect>
        <rect x="14" y="3" width="7" height="7" rx="1"></rect>
        <rect x="14" y="14" width="7" height="7" rx="1"></rect>
        <rect x="3" y="14" width="7" height="7" rx="1"></rect>
      </svg>
    )},
    { path: '/catalog', label: 'Catalog', icon: (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M6 2L3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4z"></path>
        <line x1="3" y1="6" x2="21" y2="6"></line>
        <path d="M16 10a4 4 0 0 1-8 0"></path>
      </svg>
    )},
    { path: '/orders', label: 'Orders', icon: (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <line x1="12" y1="1" x2="12" y2="23"></line>
        <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"></path>
      </svg>
    )},
    { path: '/notifications', label: 'Notifications', hasNewBadge: true, icon: (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"></path>
        <path d="M13.73 21a2 2 0 0 1-3.46 0"></path>
      </svg>
    )},
    { path: '/resources', label: 'Resources', icon: (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2z"></path>
        <path d="M22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z"></path>
      </svg>
    )},
    { path: '/profile', label: 'Profile', icon: (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
        <circle cx="12" cy="7" r="4"></circle>
      </svg>
    )},
  ];

  const userName = user?.profile?.firstName && user?.profile?.lastName 
    ? `${user.profile.firstName} ${user.profile.lastName}`
    : user?.profile?.firstName || user?.email?.split('@')?.[0] || 'User';

  return (
    <div className={styles.layout}>
      {/* Header */}
      <header className={`${styles.header} ${isScrolled ? styles.headerScrolled : ''}`}>
        <div className={styles.headerLeft}>
          <button 
            className={styles.menuBtn}
            onClick={() => setSidebarOpen(!sidebarOpen)}
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="3" y1="12" x2="21" y2="12"></line>
              <line x1="3" y1="6" x2="21" y2="6"></line>
              <line x1="3" y1="18" x2="21" y2="18"></line>
            </svg>
          </button>
          <Link to="/" className={styles.logo}>
            <img loading="eager" src="/oval-logo.webp" alt="OVAL" className={styles.logoImage} />
          </Link>
        </div>

        <div className={styles.headerRight}>
          <button
              className={styles.referralBtn}
              onClick={() => setReferralModalOpen(true)}
              title="Refer a Friend"
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M20 12v10H4V12" />
                <path d="M2 7h20v3H2z" /><path d="M12 22V7" /><path d="M12 7H7.5a2.5 2.5 0 0 1 0-5C11 2 12 7 12 7z" /><path d="M12 7h4.5a2.5 2.5 0 0 0 0-5C13 2 12 7 12 7z" />
              </svg>
              <span className={styles.referralBtnText}>Refer</span>
            </button>
          <button className={styles.notificationBtn} onClick={() => navigate('/notifications')}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"></path>
              <path d="M13.73 21a2 2 0 0 1-3.46 0"></path>
            </svg>
            {unreadNotifCount > 0 && <span className={styles.notificationDot}></span>}
          </button>
          <div className={styles.userDropdownWrapper} ref={dropdownRef}>
            <div 
              className={`${styles.userDropdown} ${dropdownOpen ? styles.userDropdownActive : ''}`}
              onClick={handleDropdownClick}
            >
              <div className={styles.userAvatar}>
                {user?.profile?.avatar ? (
                  <img loading="lazy" src={user.profile.avatar} alt={userName} />
                ) : (
                  <span>{userName[0]?.toUpperCase()}</span>
                )}
              </div>
              <span className={styles.userName}>{userName}</span>
              <svg 
                className={`${styles.chevron} ${dropdownOpen ? styles.chevronRotated : ''}`}
                width="16" 
                height="16" 
                viewBox="0 0 24 24" 
                fill="none" 
                stroke="currentColor" 
                strokeWidth="2"
              >
                <polyline points="6 9 12 15 18 9"></polyline>
              </svg>
            </div>
            {dropdownOpen && (
              <div className={styles.dropdownMenu}>
                <button 
                  className={styles.dropdownItem}
                  onClick={() => handleMenuItemClick('/profile')}
                >
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
                    <circle cx="12" cy="7" r="4"></circle>
                  </svg>
                  <span>Profile</span>
                </button>
                <button 
                  className={styles.dropdownItem}
                  onClick={() => handleMenuItemClick('/support')}
                >
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <circle cx="12" cy="12" r="10"></circle>
                    <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3"></path>
                    <line x1="12" y1="17" x2="12.01" y2="17"></line>
                  </svg>
                  <span>Contact Support</span>
                </button>
                <div className={styles.dropdownDivider} />
                <button 
                  className={styles.dropdownItem}
                  onClick={handleLogout}
                >
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"></path>
                    <polyline points="16 17 21 12 16 7"></polyline>
                    <line x1="21" y1="12" x2="9" y2="12"></line>
                  </svg>
                  <span>Log Out</span>
                </button>
              </div>
            )}
          </div>
        </div>
      </header>

      {/* Sidebar */}
      <aside className={`${styles.sidebar} ${sidebarOpen ? styles.sidebarOpen : ''} ${isScrolled ? styles.sidebarScrolled : ''}`}>
        {/* Scroll indicators for mobile */}
        <div className={`${styles.scrollIndicatorLeft} ${canScrollLeft ? styles.visible : ''}`} />
        <div className={`${styles.scrollIndicatorRight} ${canScrollRight ? styles.visible : ''}`} />

        <nav ref={navRef} className={styles.nav}>
          {/* Admin Panel - first position for admin users.
              sandboxMode is OR-ed in because isAdmin is a role-STRING check: a user on a custom
              RBAC role (users.role = role.code) fails it and would otherwise be stranded in the
              sandbox with no way back. sandboxMode can only be true if the server already
              authorised test_admin.view, so this widens nothing the backend has not allowed. */}
          {(isAdmin || sandboxMode) && !isDemo && (
            <Link
              to="/admin"
              onClick={handleExitTestDashboard}
              aria-busy={isExitingTestMode}
              className={`${styles.navLink} ${styles.adminLink} ${
                location.pathname.startsWith('/admin') ? styles.active : ''
              }`}
            >
              <span className={styles.navIcon}>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="12" cy="12" r="3"></circle>
                  <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z"></path>
                </svg>
              </span>
              <span className={styles.navLabel}>Admin Panel</span>
            </Link>
          )}

          {navItems.map((item) => (
            <Link
              key={item.path}
              to={item.path}
              className={`${styles.navLink} ${
                location.pathname === item.path ? styles.active : ''
              }`}
            >
              <span className={styles.navIcon}>{item.icon}</span>
              <span className={styles.navLabel}>{item.label}</span>
              {item.hasNewBadge && unreadNotifCount > 0 && <span className={styles.newBadge}>NEW</span>}
            </Link>
          ))}
        </nav>

        <button onClick={() => setSidebarOpen(false)} className={styles.closeMenuBtn}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <circle cx="12" cy="12" r="10"></circle>
            <line x1="15" y1="9" x2="9" y2="15"></line>
            <line x1="9" y1="9" x2="15" y2="15"></line>
          </svg>
          <span>Close Menu</span>
        </button>
      </aside>

      {/* Main Content */}
      <main className={`${styles.main} ${sidebarOpen ? styles.mainShifted : ''}`}>
        {/* One banner, two triggers (see SandboxBanner.jsx): the ORIGIN's environment
            wins, and the superseded per-user sandboxMode flag from /auth/me is passed
            through so its LIVE users keep their notice until Task 22 retires it.
            In-flow at the top of <main> so it cannot overlap the fixed header or the nav. */}
        <SandboxBanner legacyTestMode={sandboxMode} />
        {/* Tier preview toggle, pinned directly under the banner and above every dashboard page.
            It gates itself on sandboxMode (see TestTierToggle.jsx), so this is an unconditional
            mount of a component that renders nothing outside the Test Dashboard. */}
        <TestTierToggle />
        <div className={styles.content}>
          {isDemo && <DemoBanner />}
          <Outlet />
        </div>

        {/* Footer */}
        <footer className={styles.footer}>
          <span>© 2026 Oval Care Inc. All rights reserved.</span>
          <span className={styles.poweredBy}>Powered by <span className={styles.ovalLink}>Oval Care Inc.</span></span>
        </footer>
      </main>

      {/* Intercom Widget - demo gets mock chat, real users get actual Intercom */}
      <IntercomWidget
        isDemo={isDemo}
        user={user ? {
          user_id: user.id || user._id,
          email: user.email,
          name: userName,
          created_at: user.createdAt ? Math.floor(new Date(user.createdAt).getTime() / 1000) : undefined
        } : null}
      />

      {/* Referral Modal */}
      <ReferralModal
        isOpen={referralModalOpen}
        onClose={() => setReferralModalOpen(false)}
      />
    </div>
  );
};

export default DashboardLayout;
